import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session, Project } from '@/api/client';
import { api } from '@/api/client';
import {
  captureProjectListRollback,
  patchProjectListCaches,
} from '@/lib/projectListQueryCache';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import SubtitlesOffIcon from '@mui/icons-material/SubtitlesOff';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import { DescriptionIcon, CloseIcon, ArrowForwardIcon, MenuIcon, PersonOffIcon, ChatBubbleOutlineIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { SessionConfirmField } from './SessionConfirmField';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useTeleport } from '@/contexts/TeleportContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { invalidateSummariesForProjectIds } from '@/lib/summariesBatchReload';
import {
  filterReaderOtherPlannedSessions,
  getSessionReaderIds,
  formatSessionStudio,
  partitionProjectSessionsPopover,
  SESSION_PROJECT_NOT_SET_LABEL,
} from '@/utils/projectRecordingSessions';
import {
  removeSessionFromReaderPlannedCaches,
  upsertReaderPlannedSessionsCache,
} from '@/lib/readerPlannedSessionsCache';
import { SESSION_READER_NOT_SET_LABEL } from '@/constants/sessionReader';
import { getProjectTypeBadgeColor } from '@/styles/projectColorTokens';
import { SectionTitle } from '@/components/ui/SectionTitle/SectionTitle';
import { getProjectSessionsQueryOptions } from './projectSessionsQuery';
import { getReaderPlannedSessionsQueryOptions } from './readerPlannedSessionsQuery';
import { SessionCommentShell, type SessionCommentShellHandle } from '@/features/project-cell-comments/SessionCommentShell';
import { useSessionCommentSummariesForIds } from '@/hooks/useSessionCommentSummariesForRange';
import { EdgeToEdgeTabs } from '@/components/ui/EdgeToEdgeTabs/EdgeToEdgeTabs';
import {
  ProjectSessionsModalMontageRow,
  type SessionPopoverCardMenuState,
} from './ProjectSessionsModalMontageRow';
import { ProjectSessionsModalCanceledRow } from './ProjectSessionsModalCanceledRow';
import { ProjectSessionsModalOtherPlannedRow } from './ProjectSessionsModalOtherPlannedRow';
import './ProjectSessionsModal.css';
import '@/components/ui/PopoverInlineLoading.css';

interface ProjectSessionsModalProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  anchorEl: HTMLElement | null;
  /** Открыть модалку редактирования сессии (из поповера). */
  onEditSession?: (session: Session) => void;
}

type SessionPopoverTabId = 'planned' | 'past' | 'montage' | 'canceled';

const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatDateRu(ymd: string): { dayNum: number; monthName: string } {
  if (!ymd) return { dayNum: 0, monthName: '' };
  const [, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return {
    dayNum: d ?? 0,
    monthName: MONTHS_RU[(m ?? 1) - 1] ?? '',
  };
}

function formatTimeHHMM(t: string | null | undefined): string {
  if (!t) return '—';
  return t.slice(0, 5);
}

function SessionTabCountBadge({ count }: { count: number }) {
  return (
    <span
      className={`count-badge count-badge--soft${
        count > 0 ? ' count-badge--soft-warning' : ''
      }`}
    >
      {count}
    </span>
  );
}

function formatDuration(session: Session): string {
  const start = (session.start || '00:00').slice(0, 5).split(':').map(Number);
  const end = (session.end || '00:00').slice(0, 5).split(':').map(Number);
  const startMins = (start[0] ?? 0) * 60 + (start[1] ?? 0);
  const endMins = (end[0] ?? 0) * 60 + (end[1] ?? 0);
  const diff = Math.max(0, endMins - startMins);
  const hh = Math.floor(diff / 60);
  const mm = diff % 60;
  return `${hh}ч ${mm}м`;
}

function buildSessionCommentTitle(
  s: Session,
  projectName: string,
  timeStart: string,
  timeEnd: string
): string {
  const tr = `${(timeStart || '').slice(0, 5)} - ${(timeEnd || '').slice(0, 5)}`;
  if (s.session_type === 'заметка') return `${tr} — ${s.title || 'Заметка'}`;
  if (s.session_type === 'монтаж') return `${tr} — Монтаж · ${projectName}`;
  const header = s.readers?.length
    ? s.readers.map((r) => r.name).join(', ')
    : s.reader_name?.trim() || SESSION_READER_NOT_SET_LABEL;
  return `${tr} — ${header} · ${projectName}`;
}

function sortSessionsByDateStart(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => {
    const dateDiff = (a.date ?? '').localeCompare(b.date ?? '');
    if (dateDiff !== 0) return dateDiff;
    return (a.start ?? '').localeCompare(b.start ?? '');
  });
}

interface ReaderGroup {
  readerId: string | null;
  readerName: string;
  sessions: Session[];
}

type ProjectSessionsModalRecordedRowProps = {
  session: Session;
  recordingNum: number;
  project: Project;
  commentCount: number;
  cardMenu: SessionPopoverCardMenuState | null;
  setCardMenu: React.Dispatch<React.SetStateAction<SessionPopoverCardMenuState | null>>;
  sessionButtonRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
  onEditSession?: (session: Session) => void;
  requestClose: () => void;
  goToSession: (args: { id: string; date: string }) => void;
  onUnrecord: (sessionId: string, e: React.MouseEvent) => void;
};

function ProjectSessionsModalRecordedRow({
  session: s,
  recordingNum,
  project,
  commentCount,
  cardMenu,
  setCardMenu,
  sessionButtonRefs,
  onEditSession,
  requestClose,
  goToSession,
  onUnrecord,
}: ProjectSessionsModalRecordedRowProps) {
  const liRef = useRef<HTMLLIElement | null>(null);
  const shellRef = useRef<SessionCommentShellHandle | null>(null);

  const engineerName = (s.soundengineer_name ?? 'Не указан').trim() || 'Не указан';
  const { dayNum, monthName } = formatDateRu(s.date);
  const duration = formatDuration(s);
  const isPodcastSession = s.session_type === 'запись' && s.session_subtype === 'podcast';
  const podcastBadgeColor = getProjectTypeBadgeColor('Podcast');

  return (
    <li
      ref={liRef}
      className="project-sessions-modal__card project-sessions-modal__card_recorded"
      onContextMenu={(e) => {
        e.preventDefault();
        shellRef.current?.openRmb(e);
      }}
    >
      <SessionCommentShell
        ref={shellRef}
        displayContents
        popoverTriggerRef={liRef}
        sessionId={s.id}
        title={buildSessionCommentTitle(
          s,
          project.name || 'Проект',
          (s.start as string) ?? '',
          (s.end as string) ?? ''
        )}
        commentCount={commentCount}
      >
        <span className="project-sessions-modal__card-recording">
          {isPodcastSession ? (
            <>
              {recordingNum}-я запись:{' '}
              <strong style={{ background: podcastBadgeColor.bg, color: podcastBadgeColor.color, borderRadius: 4, padding: '1px 5px' }}>
                Подкаст
              </strong>
            </>
          ) : s.session_type === 'правки' ? (
            <>
              {recordingNum}-я запись: <strong className="project-sessions-modal__card-corrections">Правки</strong>
            </>
          ) : (
            <>
              {recordingNum}-я запись: <strong>{s.stop_page ?? '—'}</strong>{' '}
              <DescriptionIcon size={16} className="project-sessions-modal__card-pages-icon" aria-label="страницы" />
            </>
          )}
        </span>
        <span className="project-sessions-modal__card-meta">
          <span className="project-sessions-modal__card-engineer">{engineerName}</span>
          <span className="project-sessions-modal__card-divider" />
          <span className="project-sessions-modal__card-date">
            <span className="project-sessions-modal__card-date-line1">
              <strong>
                {dayNum} {monthName}
              </strong>
            </span>
            <span className="project-sessions-modal__card-date-line2">
              с {formatTimeHHMM(s.start)} до {formatTimeHHMM(s.end)}
            </span>
          </span>
          <span className="project-sessions-modal__card-divider" />
          <span className="project-sessions-modal__card-studio">{formatSessionStudio(s.studio)}</span>
          <span className="project-sessions-modal__card-divider" />
          <span className="project-sessions-modal__card-duration">{duration}</span>
        </span>
        <div className="project-sessions-modal__card-actions">
          <div
            ref={(el) => {
              sessionButtonRefs.current[s.id] = el ?? null;
            }}
            className="project-sessions-modal__card-menu-btn"
          >
            <IconButton
              aria-label="Меню"
              aria-haspopup="menu"
              aria-expanded={cardMenu?.session.id === s.id && cardMenu.scope === 'recorded'}
              variant="default"
              size="m"
              iconSize={18}
              onClick={(e) => {
                e.stopPropagation();
                setCardMenu((m) =>
                  m?.session.id === s.id && m.scope === 'recorded' ? null : { scope: 'recorded', session: s }
                );
              }}
              title="Меню"
            >
              <MenuIcon />
            </IconButton>
          </div>
          <ContextMenu
            isOpen={cardMenu?.session.id === s.id && cardMenu.scope === 'recorded'}
            onClose={() => setCardMenu((prev) => (prev?.session.id === s.id ? null : prev))}
            anchorPosition={null}
            anchorEl={sessionButtonRefs.current[s.id]}
            alignY="center"
            items={[
              {
                label: 'Перейти к сессии',
                icon: <ArrowForwardIcon size={18} />,
                onClick: () => {
                  requestClose();
                  goToSession({ id: s.id, date: s.date ?? '' });
                },
              },
              ...(onEditSession
                ? [
                    {
                      label: 'Редактировать',
                      onClick: () => {
                        onEditSession(s);
                        requestClose();
                      },
                    },
                  ]
                : []),
              {
                label: commentCount > 0 ? 'Посмотреть комментарии' : 'Добавить комментарий',
                icon: <ChatBubbleOutlineIcon size={18} />,
                onClick: () => {
                  shellRef.current?.openCommentThread();
                  setCardMenu(null);
                },
              },
              {
                label: 'Отменить отметку записи',
                danger: true,
                icon: <RemoveDoneIcon sx={{ fontSize: 18 }} />,
                onClick: () => {
                  onUnrecord(s.id, { stopPropagation: () => {} } as React.MouseEvent);
                },
              },
            ]}
          />
        </div>
      </SessionCommentShell>
    </li>
  );
}

function groupSessionsByReader(
  sessions: Session[],
  readers: { id: string; name: string }[],
  groupAllTogether = false
): ReaderGroup[] {
  if (groupAllTogether) {
    return sessions.length > 0 ? [{ readerId: null, readerName: 'Чтецы', sessions }] : [];
  }
  const withoutReader = sessions.filter((s) => getSessionReaderIds(s).length === 0);

  if (!readers.length) {
    return withoutReader.length > 0
      ? [{ readerId: null, readerName: SESSION_READER_NOT_SET_LABEL, sessions: withoutReader }]
      : [];
  }

  if (readers.length === 1) {
    const reader = readers[0];
    const readerSessions = sessions.filter((s) => getSessionReaderIds(s).includes(reader.id));
    const result: ReaderGroup[] = [
      { readerId: reader.id, readerName: reader.name, sessions: readerSessions },
    ];
    if (withoutReader.length > 0) {
      result.push({ readerId: null, readerName: SESSION_READER_NOT_SET_LABEL, sessions: withoutReader });
    }
    return result;
  }

  const groups = new Map<string | null, Session[]>();
  for (const s of sessions) {
    const ids = getSessionReaderIds(s);
    for (const key of ids.length > 0 ? ids : [null]) {
      const list = groups.get(key) ?? [];
      list.push(s);
      groups.set(key, list);
    }
  }

  const result: ReaderGroup[] = readers.map((r) => ({
    readerId: r.id,
    readerName: r.name,
    sessions: groups.get(r.id) ?? [],
  }));
  if (withoutReader.length > 0) {
    result.push({ readerId: null, readerName: SESSION_READER_NOT_SET_LABEL, sessions: withoutReader });
  }
  return result;
}

export function ProjectSessionsModal({ open, onClose, project, anchorEl, onEditSession }: ProjectSessionsModalProps) {
  const queryClient = useQueryClient();
  const { goToSession } = useTeleport();
  const { showError: showToastError } = useToast();
  const popoverRef = useRef<HTMLDivElement>(null);
  const readers = useMemo(() => project.readers ?? [], [project.readers]);
  const readerIdsForOther = useMemo(
    () => readers.map((r) => r.id).filter(Boolean).sort(),
    [readers]
  );

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  const {
    data: fetchedSessions,
    isPending: sessionsPending,
    isError: sessionsError,
    error: sessionsFetchError,
  } = useQuery({
    ...getProjectSessionsQueryOptions(project.id),
    enabled: shouldRender,
  });

  const {
    data: otherSessionsRaw,
    isPending: otherSessionsPending,
    isError: otherSessionsError,
  } = useQuery({
    ...getReaderPlannedSessionsQueryOptions(readerIdsForOther, project.id),
    enabled: shouldRender && readerIdsForOther.length > 0,
  });

  const sessionsRaw = useMemo(
    () => fetchedSessions ?? project.sessions ?? [],
    [fetchedSessions, project.sessions]
  );

  const {
    plannedSessions,
    recordedSessions,
    montageSessions,
    canceledSessions,
  } = useMemo(() => {
    const parts = partitionProjectSessionsPopover(sessionsRaw);
    const planned = [...parts.planned].sort((a, b) => {
      if (a.date !== b.date) return a.date > b.date ? 1 : -1;
      return (a.start ?? '') > (b.start ?? '') ? 1 : -1;
    });
    const recorded = [...parts.past].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return (a.start ?? '') < (b.start ?? '') ? -1 : 1;
    });
    const montage = sortSessionsByDateStart(parts.montage);
    const canceled = sortSessionsByDateStart(parts.canceled);
    return {
      plannedSessions: planned,
      recordedSessions: recorded,
      montageSessions: montage,
      canceledSessions: canceled,
    };
  }, [sessionsRaw]);

  const sessions = sessionsRaw;

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    /** Меню карточки и тред комментариев (портал на `body` вне `popoverRef`). */
    excludeSelector: '.context-menu, [data-cell-comments-thread-root="true"]',
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-cell-comments-thread-root="true"]')) {
        return;
      }
      requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const [expandedPlannedId, setExpandedPlannedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SessionPopoverTabId>('planned');
  const [cardMenu, setCardMenu] = useState<SessionPopoverCardMenuState | null>(null);
  const sessionButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const plannedSessionCommentShellRef = useRef<Record<string, SessionCommentShellHandle | null>>({});
  const [engineerDisplayBySessionId, setEngineerDisplayBySessionId] = useState<Record<string, string>>({});
  const [draftBySessionId, setDraftBySessionId] = useState<Record<string, { start?: string; end?: string; studio?: number }>>({});

  useEffect(() => {
    if (!shouldRender) {
      setCardMenu(null);
      setActiveTab('planned');
    }
  }, [shouldRender]);

  const recordedByReader = useMemo(
    () => groupSessionsByReader(recordedSessions, readers.map((r) => ({ id: r.id, name: r.name })), project.project_type === 'Podcast'),
    [recordedSessions, readers, project.project_type]
  );
  const plannedByReader = useMemo(
    () => groupSessionsByReader(plannedSessions, readers.map((r) => ({ id: r.id, name: r.name })), project.project_type === 'Podcast'),
    [plannedSessions, readers, project.project_type]
  );
  /** Без групп чтецов без запланированных сессий — иначе показываются лишние разделители и «Нет записей». */
  const plannedByReaderNonEmpty = useMemo(
    () => plannedByReader.filter((g) => g.sessions.length > 0),
    [plannedByReader]
  );

  const otherPlannedSessions = useMemo(
    () => filterReaderOtherPlannedSessions(otherSessionsRaw ?? [], readerIdsForOther, project.id),
    [otherSessionsRaw, readerIdsForOther, project.id]
  );

  const otherPlannedByReaderNonEmpty = useMemo(() => {
    const groups = groupSessionsByReader(
      otherPlannedSessions,
      readers.map((r) => ({ id: r.id, name: r.name })),
      project.project_type === 'Podcast'
    );
    return groups.filter((g) => g.sessions.length > 0);
  }, [otherPlannedSessions, readers, project.project_type]);

  const allModalSessionIds = useMemo(
    () =>
      [...sessions, ...otherPlannedSessions]
        .map((s) => s.id)
        .filter((id) => id && !id.startsWith('temp-')),
    [sessions, otherPlannedSessions]
  );
  const { bySessionId: sessionCommentCountById } = useSessionCommentSummariesForIds(
    allModalSessionIds,
    shouldRender && allModalSessionIds.length > 0
  );

  const getEarliestPlannedIndexPerReader = useMemo(() => {
    const map = new Map<string | null, number>();
    plannedSessions.forEach((s, i) => {
      const key = getSessionReaderIds(s)[0] ?? null;
      if (!map.has(key)) map.set(key, i);
    });
    return map;
  }, [plannedSessions]);

  const syncSessionIntoRangeCaches = useCallback(
    (session: Session) => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const qFrom = queryKey[1] as string | undefined;
        const qTo = queryKey[2] as string | undefined;
        const inRange = qFrom && qTo && session.date >= qFrom && session.date <= qTo;
        const exists = old.some((item) => item.id === session.id);

        if (inRange) {
          const next = exists
            ? old.map((item) => (item.id === session.id ? session : item))
            : sortSessionsByDateStart([...old, session]);
          if (next.some((item, index) => item !== old[index]) || next.length !== old.length) {
            queryClient.setQueryData<Session[]>(queryKey, next);
          }
          continue;
        }

        if (exists) {
          queryClient.setQueryData<Session[]>(queryKey, old.filter((item) => item.id !== session.id));
        }
      }
    },
    [queryClient]
  );

  const removeSessionFromRangeCaches = useCallback(
    (sessionId: string) => {
      const entries = queryClient.getQueriesData<Session[]>({ queryKey: ['sessions'] });
      for (const [queryKey, old] of entries) {
        if (!old) continue;
        const next = old.filter((session) => session.id !== sessionId);
        if (next.length !== old.length) {
          queryClient.setQueryData<Session[]>(queryKey, next);
        }
      }
    },
    [queryClient]
  );

  const handleSaved = useCallback(
    (params?: { updatedSession?: Session; deletedSessionId?: string }) => {
      if (params?.updatedSession) {
        const updatedSession = params.updatedSession;
        upsertReaderPlannedSessionsCache(queryClient, updatedSession);
        patchProjectListCaches(queryClient, (prev) =>
          prev
            ? prev.map((p) =>
                p.id !== project.id
                  ? p
                  : {
                      ...p,
                      sessions: (p.sessions ?? []).map((s) =>
                        s.id === updatedSession.id ? updatedSession : s
                      ),
                    }
              )
            : prev
        );
        queryClient.setQueryData<Session[]>(['projectSessions', project.id], (old) =>
          sortSessionsByDateStart(
            updatedSession.project_id !== project.id
              ? (old ?? project.sessions ?? []).filter((s) => s.id !== updatedSession.id)
              : (old ?? project.sessions ?? []).some((s) => s.id === updatedSession.id)
                ? (old ?? project.sessions ?? []).map((s) => (s.id === updatedSession.id ? updatedSession : s))
                : [...(old ?? project.sessions ?? []), updatedSession]
          )
        );
        syncSessionIntoRangeCaches(updatedSession);
      }
      if (params?.deletedSessionId) {
        removeSessionFromRangeCaches(params.deletedSessionId);
        removeSessionFromReaderPlannedCaches(queryClient, params.deletedSessionId);
      }
      invalidateSummariesForProjectIds(queryClient, [project.id]);
      setExpandedPlannedId(null);
    },
    [project.id, project.sessions, queryClient, removeSessionFromRangeCaches, syncSessionIntoRangeCaches]
  );

  const handleSavedSession = useCallback(
    (session: Session) => {
      handleSaved({ updatedSession: session });
    },
    [handleSaved]
  );

  const handleEngineerFromAuth = useCallback((sessionId: string, displayName: string) => {
    setEngineerDisplayBySessionId((prev) => ({ ...prev, [sessionId]: displayName }));
  }, []);

  const handleDraftChange = useCallback((sessionId: string, draft: { start?: string; end?: string; studio?: number }) => {
    setDraftBySessionId((prev) => ({ ...prev, [sessionId]: draft }));
  }, []);

  const handleUnrecord = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deletingId) return;
    if (!window.confirm('Отменить отметку записи? Сессия вернётся в «Запланированные».')) return;
    setDeletingId(sessionId);
    const rollback = captureProjectListRollback(queryClient);
    patchProjectListCaches(queryClient, (prev) =>
      prev
        ? prev.map((p) =>
            p.id !== project.id
              ? p
              : {
                  ...p,
                  sessions: (p.sessions ?? []).map((s) =>
                    s.id === sessionId ? { ...s, is_recorded: false } : s
                  ),
                }
          )
        : prev
    );
    try {
      const updatedSession = await api.sessions.update(sessionId, { is_recorded: false });
      handleSaved({ updatedSession });
    } catch (err) {
      void rollbackProjectsAndReconcile(queryClient, rollback).catch(() => {});
      showToastError((err as Error).message || 'Ошибка');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePlannedClearProject = useCallback(
    async (s: Session) => {
      if (!s.project_id) return;
      if (!window.confirm('Убрать проект у этой сессии? Сессия останется в расписании без привязки к проекту.')) return;
      const rollback = captureProjectListRollback(queryClient);
      patchProjectListCaches(queryClient, (prev) =>
        prev
          ? prev.map((p) =>
              p.id !== project.id
                ? p
                : { ...p, sessions: (p.sessions ?? []).filter((x) => x.id !== s.id) }
            )
          : prev
      );
      queryClient.setQueryData<Session[]>(['projectSessions', project.id], (old) =>
        (old ?? []).filter((x) => x.id !== s.id)
      );
      upsertReaderPlannedSessionsCache(queryClient, { ...s, project_id: null, project_name: null });
      try {
        const updatedSession = await api.sessions.update(s.id, { project_id: null });
        handleSaved({ updatedSession });
      } catch (err) {
        void rollbackProjectsAndReconcile(queryClient, rollback).catch(() => {});
        showToastError((err as Error).message || 'Ошибка');
        upsertReaderPlannedSessionsCache(queryClient, s);
        void api.sessions.list({ projectId: project.id }).then((list) => {
          queryClient.setQueryData(['projectSessions', project.id], list);
        });
      }
    },
    [queryClient, project.id, handleSaved, showToastError]
  );

  const handlePlannedClearReader = useCallback(
    async (s: Session) => {
      if (getSessionReaderIds(s).length === 0) return;
      if (!window.confirm('Убрать чтеца у этой сессии?')) return;
      const rollback = captureProjectListRollback(queryClient);
      patchProjectListCaches(queryClient, (prev) =>
        prev
          ? prev.map((p) =>
              p.id !== project.id
                ? p
                : {
                    ...p,
                    sessions: (p.sessions ?? []).map((x) =>
                      x.id === s.id ? { ...x, reader_id: null, reader_name: null, readers: [], reader_ids: [] } : x
                    ),
                  }
            )
          : prev
      );
      queryClient.setQueryData<Session[]>(['projectSessions', project.id], (old) =>
        (old ?? []).map((x) => (x.id === s.id ? { ...x, reader_id: null, reader_name: null, readers: [], reader_ids: [] } : x))
      );
      try {
        const updatedSession = await api.sessions.update(s.id, { reader_ids: [] });
        handleSaved({ updatedSession });
      } catch (err) {
        void rollbackProjectsAndReconcile(queryClient, rollback).catch(() => {});
        showToastError((err as Error).message || 'Ошибка');
        void api.sessions.list({ projectId: project.id }).then((list) => {
          queryClient.setQueryData(['projectSessions', project.id], list);
        });
      }
    },
    [queryClient, project.id, handleSaved, showToastError]
  );

  const handlePlannedDelete = useCallback(
    async (s: Session) => {
      if (!window.confirm('Удалить запланированную сессию?')) return;
      setDeletingId(s.id);
      const rollback = captureProjectListRollback(queryClient);
      patchProjectListCaches(queryClient, (prev) =>
        prev
          ? prev.map((p) =>
              p.id !== project.id
                ? p
                : { ...p, sessions: (p.sessions ?? []).filter((x) => x.id !== s.id) }
            )
          : prev
      );
      queryClient.setQueryData<Session[]>(['projectSessions', project.id], (old) =>
        (old ?? []).filter((x) => x.id !== s.id)
      );
      removeSessionFromReaderPlannedCaches(queryClient, s.id);
      try {
        await api.sessions.delete(s.id);
        handleSaved({ deletedSessionId: s.id });
      } catch (err) {
        void rollbackProjectsAndReconcile(queryClient, rollback).catch(() => {});
        showToastError((err as Error).message || 'Ошибка');
        void api.sessions.list({ projectId: project.id }).then((list) => {
          queryClient.setQueryData(['projectSessions', project.id], list);
        });
      } finally {
        setDeletingId(null);
      }
    },
    [queryClient, project.id, handleSaved, showToastError]
  );

  const isEarliestForReader = (session: Session): boolean => {
    const idx = plannedSessions.findIndex((s) => s.id === session.id);
    if (project.project_type === 'Podcast') {
      return idx === 0;
    }
    const earliestIdx = getEarliestPlannedIndexPerReader.get(getSessionReaderIds(session)[0] ?? null);
    return idx >= 0 && earliestIdx === idx;
  };

  const sessionPopoverTabItems = useMemo(
    () => [
      {
        id: 'planned',
        label: 'Запланированные',
        adornment: <SessionTabCountBadge count={plannedSessions.length} />,
      },
      {
        id: 'past',
        label: 'Прошедшие',
        adornment: <SessionTabCountBadge count={recordedSessions.length} />,
      },
      {
        id: 'montage',
        label: 'Монтаж',
        adornment: <SessionTabCountBadge count={montageSessions.length} />,
      },
      {
        id: 'canceled',
        label: 'Отменённые',
        adornment: <SessionTabCountBadge count={canceledSessions.length} />,
      },
    ],
    [plannedSessions.length, recordedSessions.length, montageSessions.length, canceledSessions.length]
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-sessions-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-sessions-popover-title"
        >
      <div className="project-sessions-popover__header">
        <div className="project-sessions-popover__header-top">
          <h2 id="project-sessions-popover-title" className="project-sessions-popover__title">
            Записи проекта: {project.name || 'Название проекта'}
          </h2>
          <IconButton aria-label="Закрыть" onClick={requestClose} size="m" iconSize={20} variant="danger" className="project-sessions-popover__close">
            <CloseIcon />
          </IconButton>
        </div>
        {!(sessionsError && fetchedSessions === undefined) ? (
          <div className="project-sessions-popover__tabs">
            <EdgeToEdgeTabs
              aria-label="Разделы записей проекта"
              items={sessionPopoverTabItems}
              value={activeTab}
              onChange={(id) => setActiveTab(id as SessionPopoverTabId)}
            />
          </div>
        ) : null}
      </div>
      <OverlayScrollArea
        className="project-sessions-popover__body"
        osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
      >
        {sessionsPending && fetchedSessions === undefined ? (
          <div className="project-popover-inline-loading" aria-busy="true" aria-live="polite">
            <span className="project-popover-inline-loading__spinner" aria-label="Загрузка сессий" />
          </div>
        ) : sessionsError && fetchedSessions === undefined ? (
          <p className="project-sessions-modal__empty" role="alert">
            {(sessionsFetchError as Error)?.message || 'Не удалось загрузить сессии'}
          </p>
        ) : (
        <div
          className={
            activeTab === 'montage' || activeTab === 'canceled'
              ? 'project-sessions-modal project-sessions-modal_body-tab-no-groups'
              : 'project-sessions-modal'
          }
        >
          <div className="project-sessions-modal__sections">
          {activeTab === 'past' && (
          <section className="project-sessions-modal__section">
            {recordedSessions.length === 0 ? (
              <SectionTitle as="p" className="project-sessions-modal__empty-past-title">
                Нет прошедших сессий
              </SectionTitle>
            ) : (
              <div className="project-sessions-modal__groups">
                {recordedByReader.map((group, gi) => (
                  <div key={group.readerId ?? 'null'} className="project-sessions-modal__group project-sessions-modal__group_recorded">
                    <div className={`project-sessions-modal__group-header ${gi > 0 ? 'project-sessions-modal__group-header_not-first' : ''}`}>
                      <span className="project-sessions-modal__group-line" />
                      <span
                        className={`project-sessions-modal__badge${group.readerId == null ? ' project-sessions-modal__badge_reader-unset' : ''}`}
                      >
                        {group.readerName}
                      </span>
                      <span className="project-sessions-modal__group-line" />
                    </div>
                    <ul className="project-sessions-modal__list">
                      {group.sessions.length === 0 ? (
                        <li className="project-sessions-modal__empty project-sessions-modal__empty_inline">Нет записей</li>
                      ) : group.sessions.map((s, si) => {
                        const firstIdx = recordedByReader.slice(0, gi).reduce((sum, g) => sum + g.sessions.length, 0);
                        const recordingNum = firstIdx + si + 1;
                        return (
                          <ProjectSessionsModalRecordedRow
                            key={s.id}
                            session={s}
                            recordingNum={recordingNum}
                            project={project}
                            commentCount={sessionCommentCountById[s.id] ?? 0}
                            cardMenu={cardMenu}
                            setCardMenu={setCardMenu}
                            sessionButtonRefs={sessionButtonRefs}
                            onEditSession={onEditSession}
                            requestClose={requestClose}
                            goToSession={goToSession}
                            onUnrecord={handleUnrecord}
                          />
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
          )}

          {activeTab === 'planned' && (
          <>
          <section className="project-sessions-modal__section">
            {plannedSessions.length === 0 ? (
              <SectionTitle as="p" className="project-sessions-modal__empty-planned-title">
                Нет запланированных сессий
              </SectionTitle>
            ) : (
              <div className="project-sessions-modal__groups">
                {plannedByReaderNonEmpty.map((group, gi) => (
                  <div key={group.readerId ?? 'null'} className="project-sessions-modal__group">
                    <div className={`project-sessions-modal__group-header ${gi > 0 ? 'project-sessions-modal__group-header_not-first' : ''}`}>
                      <span className="project-sessions-modal__group-line" />
                      <span
                        className={`project-sessions-modal__badge${group.readerId == null ? ' project-sessions-modal__badge_reader-unset' : ''}`}
                      >
                        {group.readerName}
                      </span>
                      <span className="project-sessions-modal__group-line" />
                    </div>
                    <ul className="project-sessions-modal__list">
                      {group.sessions.map((s) => {
                        const engineerDisplay = (engineerDisplayBySessionId[s.id] ?? s.soundengineer_name ?? 'Не указан').trim() || 'Не указан';
                        const draft = draftBySessionId[s.id];
                        const displayStart = draft?.start ?? s.start;
                        const displayEnd = draft?.end ?? s.end;
                        const displayStudio = draft?.studio ?? (typeof s.studio === 'number' ? s.studio : parseInt(String(s.studio || ''), 10) || undefined);
                        const { dayNum, monthName } = formatDateRu(s.date);
                        const isExpanded = expandedPlannedId === s.id;
                        const canExpand = isEarliestForReader(s);

                        const handleToggle = () => {
                          if (!canExpand) return;
                          if (isExpanded) {
                            setExpandedPlannedId(null);
                            setEngineerDisplayBySessionId((prev) => {
                              const next = { ...prev };
                              delete next[s.id];
                              return next;
                            });
                            setDraftBySessionId((prev) => {
                              const next = { ...prev };
                              delete next[s.id];
                              return next;
                            });
                          } else {
                            setExpandedPlannedId(s.id);
                          }
                        };

                        return (
                          <li
                            key={s.id}
                            className={`project-sessions-modal__card project-sessions-modal__card_planned project-sessions-modal__card_planned--comments-thread ${isExpanded ? 'project-sessions-modal__card_expanded' : ''} ${!canExpand ? 'project-sessions-modal__card_disabled' : ''}`}
                            onClick={canExpand ? handleToggle : undefined}
                            role={canExpand ? 'button' : undefined}
                            title={!canExpand ? 'Можно внести только самую раннюю запись для этого чтеца' : undefined}
                          >
                            <SessionCommentShell
                              ref={(h) => {
                                plannedSessionCommentShellRef.current[s.id] = h;
                              }}
                              className="project-sessions-modal__session-comment-shell"
                              sessionId={s.id}
                              title={buildSessionCommentTitle(
                                s,
                                project.name || 'Проект',
                                formatTimeHHMM(String(displayStart)),
                                formatTimeHHMM(String(displayEnd))
                              )}
                              commentCount={sessionCommentCountById[s.id] ?? 0}
                            >
                            <div className="project-sessions-modal__card-meta-row">
                              {s.session_type === 'правки' ? (
                                <strong className="project-sessions-modal__card-corrections project-sessions-modal__card-planned-corrections-label">
                                  Правки
                                </strong>
                              ) : null}
                              <span className="project-sessions-modal__card-meta">
                                <span className="project-sessions-modal__card-date">
                                  <span className="project-sessions-modal__card-date-line1"><strong>{dayNum} {monthName}</strong></span>
                                  <span className="project-sessions-modal__card-date-line2">с {formatTimeHHMM(displayStart)} до {formatTimeHHMM(displayEnd)}</span>
                                </span>
                                <span className="project-sessions-modal__card-studio">
                                  {formatSessionStudio(displayStudio)}
                                </span>
                                <span className="project-sessions-modal__card-engineer">{engineerDisplay}</span>
                              </span>
                              <div
                                className="project-sessions-modal__card-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  ref={(el) => {
                                    sessionButtonRefs.current[s.id] = el ?? null;
                                  }}
                                  className="project-sessions-modal__card-menu-btn"
                                >
                                  <IconButton
                                    aria-label="Меню"
                                    aria-haspopup="menu"
                                    aria-expanded={cardMenu?.session.id === s.id && cardMenu.scope === 'planned'}
                                    variant="default"
                                    size="m"
                                    iconSize={18}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCardMenu((m) =>
                                        m?.session.id === s.id && m.scope === 'planned'
                                          ? null
                                          : { scope: 'planned', session: s }
                                      );
                                    }}
                                    title="Меню"
                                  >
                                    <MenuIcon />
                                  </IconButton>
                                </div>
                                <ContextMenu
                                  isOpen={cardMenu?.session.id === s.id && cardMenu.scope === 'planned'}
                                  onClose={() => setCardMenu((prev) => (prev?.session.id === s.id ? null : prev))}
                                  anchorPosition={null}
                                  anchorEl={sessionButtonRefs.current[s.id]}
                                  alignY="center"
                                  anchorPriorityAxis="y"
                                  items={[
                                    {
                                      label: 'Перейти к сессии',
                                      icon: <ArrowForwardIcon size={18} />,
                                      onClick: () => {
                                        requestClose();
                                        goToSession({ id: s.id, date: s.date ?? '' });
                                      },
                                    },
                                    ...(onEditSession
                                      ? [
                                          {
                                            label: 'Редактировать',
                                            onClick: () => {
                                              onEditSession(s);
                                              requestClose();
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      label: (sessionCommentCountById[s.id] ?? 0) > 0 ? 'Посмотреть комментарии' : 'Добавить комментарий',
                                      icon: <ChatBubbleOutlineIcon size={18} />,
                                      onClick: () => {
                                        plannedSessionCommentShellRef.current[s.id]?.openCommentThread();
                                        setCardMenu(null);
                                      },
                                    },
                                    {
                                      label: 'Очистить проект',
                                      icon: <SubtitlesOffIcon sx={{ fontSize: 18 }} />,
                                      danger: true,
                                      disabled: !s.project_id,
                                      onClick: () => {
                                        void handlePlannedClearProject(s);
                                      },
                                    },
                                    ...(s.session_type === 'запись' || s.session_type === 'правки'
                                      ? [
                                          {
                                            label: 'Очистить чтеца',
                                            icon: <PersonOffIcon size={18} />,
                                            danger: true,
                                            disabled: getSessionReaderIds(s).length === 0,
                                            onClick: () => {
                                              void handlePlannedClearReader(s);
                                            },
                                          },
                                        ]
                                      : []),
                                    {
                                      label: 'Удалить сессию',
                                      danger: true,
                                      onClick: () => {
                                        void handlePlannedDelete(s);
                                      },
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                            <div
                              className={`project-sessions-modal__card-expand-wrapper ${isExpanded ? 'project-sessions-modal__card-expand-wrapper_expanded' : ''}`}
                            >
                              <div
                                className="project-sessions-modal__card-expand"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SessionConfirmField
                                  session={s}
                                  project={project}
                                  projectSessions={sessions}
                                  onSaved={handleSavedSession}
                                  isExpanded={isExpanded}
                                  onEngineerFromAuth={(name) => handleEngineerFromAuth(s.id, name)}
                                  onDraftChange={handleDraftChange}
                                />
                              </div>
                            </div>
                            </SessionCommentShell>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
          {readerIdsForOther.length > 0 &&
            ((otherSessionsPending && otherSessionsRaw == null) ||
              otherSessionsError ||
              otherPlannedByReaderNonEmpty.length > 0) && (
            <section className="project-sessions-modal__section project-sessions-modal__section_other-planned">
              {otherSessionsPending && otherSessionsRaw == null ? (
                <div className="project-popover-inline-loading" aria-busy="true" aria-live="polite">
                  <span className="project-popover-inline-loading__spinner" aria-label="Загрузка сессий других проектов" />
                </div>
              ) : otherSessionsError ? (
                <p className="project-sessions-modal__other-planned-status" role="alert">
                  Не удалось загрузить сессии других проектов
                </p>
              ) : otherPlannedByReaderNonEmpty.length > 0 ? (
                <>
                  <SectionTitle as="h3" className="project-sessions-modal__other-planned-title">
                    Запланированные на другие проекты
                  </SectionTitle>
                  <div className="project-sessions-modal__groups">
                    {otherPlannedByReaderNonEmpty.map((group, gi) => (
                      <div key={`other-${group.readerId ?? 'null'}`} className="project-sessions-modal__group">
                        <div className={`project-sessions-modal__group-header ${gi > 0 ? 'project-sessions-modal__group-header_not-first' : ''}`}>
                          <span className="project-sessions-modal__group-line" />
                          <span
                            className={`project-sessions-modal__badge${group.readerId == null ? ' project-sessions-modal__badge_reader-unset' : ''}`}
                          >
                            {group.readerName}
                          </span>
                          <span className="project-sessions-modal__group-line" />
                        </div>
                        <ul className="project-sessions-modal__list">
                          {group.sessions.map((s) => {
                            const projectLabel =
                              s.project_id == null
                                ? SESSION_PROJECT_NOT_SET_LABEL
                                : (s.project_name?.trim() || 'Проект');
                            const { dayNum, monthName } = formatDateRu(s.date);
                            const engineerDisplay =
                              (s.soundengineer_name ?? 'Не указан').trim() || 'Не указан';
                            return (
                              <ProjectSessionsModalOtherPlannedRow
                                key={s.id}
                                session={s}
                                commentCount={sessionCommentCountById[s.id] ?? 0}
                                commentTitle={buildSessionCommentTitle(
                                  s,
                                  projectLabel,
                                  formatTimeHHMM(s.start),
                                  formatTimeHHMM(s.end)
                                )}
                                projectLabel={projectLabel}
                                dayNum={dayNum}
                                monthName={monthName}
                                timeStart={formatTimeHHMM(s.start)}
                                timeEnd={formatTimeHHMM(s.end)}
                                studioLabel={formatSessionStudio(s.studio)}
                                engineerName={engineerDisplay}
                                cardMenu={cardMenu}
                                setCardMenu={setCardMenu}
                                sessionButtonRefs={sessionButtonRefs}
                                onEditSession={onEditSession}
                                requestClose={requestClose}
                                goToSession={goToSession}
                              />
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </section>
          )}
          </>
          )}

          {activeTab === 'montage' && (
          <section className="project-sessions-modal__section">
            {montageSessions.length === 0 ? (
              <SectionTitle as="p" className="project-sessions-modal__empty-planned-title">
                Нет сессий монтажа
              </SectionTitle>
            ) : (
              <ul className="project-sessions-modal__list">
                {montageSessions.map((s) => {
                  const { dayNum, monthName } = formatDateRu(s.date ?? '');
                  const engineerName = (s.soundengineer_name ?? 'Не указан').trim() || 'Не указан';
                  return (
                    <ProjectSessionsModalMontageRow
                      key={s.id}
                      session={s}
                      commentCount={sessionCommentCountById[s.id] ?? 0}
                      commentTitle={buildSessionCommentTitle(
                        s,
                        project.name || 'Проект',
                        formatTimeHHMM(String(s.start)),
                        formatTimeHHMM(String(s.end))
                      )}
                      dayNum={dayNum}
                      monthName={monthName}
                      timeStart={formatTimeHHMM(s.start)}
                      timeEnd={formatTimeHHMM(s.end)}
                      studioLabel={formatSessionStudio(s.studio)}
                      engineerName={engineerName}
                      cardMenu={cardMenu}
                      setCardMenu={setCardMenu}
                      sessionButtonRefs={sessionButtonRefs}
                      onEditSession={onEditSession}
                      requestClose={requestClose}
                      goToSession={goToSession}
                    />
                  );
                })}
              </ul>
            )}
          </section>
          )}

          {activeTab === 'canceled' && (
          <section className="project-sessions-modal__section">
            {canceledSessions.length === 0 ? (
              <SectionTitle as="p" className="project-sessions-modal__empty-planned-title">
                Нет отменённых сессий
              </SectionTitle>
            ) : (
              <ul className="project-sessions-modal__list">
                {canceledSessions.map((s) => {
                  const { dayNum, monthName } = formatDateRu(s.date ?? '');
                  return (
                    <ProjectSessionsModalCanceledRow
                      key={s.id}
                      session={s}
                      commentCount={sessionCommentCountById[s.id] ?? 0}
                      commentTitle={buildSessionCommentTitle(
                        s,
                        project.name || 'Проект',
                        formatTimeHHMM(String(s.start)),
                        formatTimeHHMM(String(s.end))
                      )}
                      dayNum={dayNum}
                      monthName={monthName}
                      timeStart={formatTimeHHMM(s.start)}
                      timeEnd={formatTimeHHMM(s.end)}
                      studioLabel={formatSessionStudio(s.studio)}
                    />
                  );
                })}
              </ul>
            )}
          </section>
          )}
        </div>
        </div>
        )}
      </OverlayScrollArea>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  if (!shouldRender) return null;
  return createPortal(popoverContent, document.body);
}
